import { ampli } from '@/ampli';

import { fontStyles } from '@/styles/typography';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipTrigger,
  MatchaTooltipPopover,
  MatchaTooltipBottomSheet,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';

import {
  SECURITY_LEVEL_TO_BADGE_VARIANT,
  getSecurityLabel,
  useSecurityAssessment,
} from '../../../hooks/useSecurityAssessment';
import { BadgeIcon } from './RiskIcons/BadgeIcon';
import {
  SECURITY_LEVEL_GRADIENT,
  securityLevelContainerBg,
  securityLevelTextColor,
} from './styles';

type SecurityAuditPreviewProps = {
  activeToken: TokenResult;
};

export const SecurityAuditPreview = ({ activeToken }: SecurityAuditPreviewProps) => {
  const { level, risks, highRisks } = useSecurityAssessment({ activeToken });

  const totalRisks = risks + highRisks;
  const securityLabel = getSecurityLabel(level, totalRisks);

  const handleScoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('securityAudit')?.scrollIntoView({ behavior: 'smooth' });

    ampli.securityAuditClicked({
      token_address: activeToken.address,
      token_chain_id: activeToken.chainId,
      token_chain_name: activeToken.chainName,
      token_symbol: activeToken.symbol,
    });
  };

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <div
          onClick={handleScoreClick}
          className={fontStyles({
            variant: 'LABEL_SM',
            class: [
              'items-center rounded-2xl flex justify-center h-6 px-2.5 gap-1.5',
              'outline-none bg-token-alphalight-300 hover:bg-token-alphalight-400',
              'active:bg-token-alphalight-400 focus-visible:outline-token-alphalight-500',
              'focus-visible:outline-2 focus-visible:outline-solid cursor-pointer',
            ],
          })}
        >
          <BadgeIcon variant={SECURITY_LEVEL_TO_BADGE_VARIANT[level]} />
          {level === 'Pending' ? '-' : totalRisks}
        </div>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>
        <div className="block max-w-[224px]">
          <h2
            className={fontStyles({
              variant: 'LABEL_SM',
              class: ['mb-2', 'hidden', 'tablet:flex', 'text-token-gray-1600'],
            })}
          >
            Security Audit
          </h2>
          <div className="flex flex-col">
            <div
              className={securityLevelContainerBg({
                level,
                class: [
                  'flex flex-col items-center justify-center rounded-[16px] relative border',
                  'overflow-hidden border-solid min-h-[106px] border-primitive-basic-black/6',
                ],
              })}
            >
              <div
                className="rotate-[-16.841deg] absolute top-[-25%] left-0 w-full h-full overflow-hidden pointer-events-none x-0"
                style={SECURITY_LEVEL_GRADIENT[level]}
              />
              <BadgeIcon size={32} variant={SECURITY_LEVEL_TO_BADGE_VARIANT[level]} />
              <p
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: ['mt-2 flex justify-center', securityLevelTextColor({ level })],
                })}
              >
                {securityLabel}
              </p>
            </div>
            <span className={fontStyles({ variant: 'BODY_SM', class: 'mt-2 text-token-gray-800' })}>
              Audits can miss issues. Results are not verified by Matcha. Always conduct your own
              research.
            </span>
          </div>
        </div>
      </MatchaTooltipContent>
      <MatchaTooltipPopover align="start" className="py-3 block" side="right" sideOffset={8} />
      <MatchaTooltipBottomSheet header="Token Risks" />
    </MatchaTooltip>
  );
};
